import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Accordion,
  Alert,
  Button,
  Form,
  Modal,
  Nav,
  Tab,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faEnvelope,
  faPen,
  faPhone,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { useDemo, type Action, type Customer } from "@/context/DemoContext";
import { useAuth } from "@/context/AuthContext";
import { adminRoles } from "@/helpers/roles";
import { Field } from "@/components/common/Field";

import { useCrmHub } from "@/context/SignalRContext";

type Pane = "complete" | "add" | "order" | "reschedule" | "reassign";
const titles: Record<Pane, string> = {
  complete: "Complete Action",
  add: "Add Customer Action",
  order: "New submission",
  reschedule: "Reschedule Action",
  reassign: "Reassign Action",
};
export function actionStatus(action: Action) {
  return action.closed
    ? "Completed"
    : action.due.slice(0, 10) < "2026-10-08"
      ? "Overdue"
      : action.due.slice(0, 10) === "2026-10-08"
        ? "Due today"
        : "Pending";
}
export function ActionWorkspace({
  actionId,
  onClose,
}: {
  actionId: string;
  onClose: () => void;
}) {
  const { actions, setActions, customers, setCustomers, entries, setEntries } =
    useDemo();
  const { session } = useAuth();
  const { viewAction } = useCrmHub();
  const action = actions.find((a) => a.id === actionId);
  const customer = customers.find((c) => c.id === action?.customerId);
  const [pane, setPane] = useState<Pane | null>(null);
  const [tab, setTab] = useState("details");
  const [addingNote, setAddingNote] = useState(false);
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(false);
  const [actionText, setActionText] = useState(action?.text ?? "");
  const [message, setMessage] = useState("");
  const workflowTitle = useRef<HTMLHeadingElement>(null);
  const notesTitle = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (action && !action.closed) return viewAction(actionId);
  }, [actionId, action?.closed, viewAction]);
  if (!action || !customer) return null;
  const status = actionStatus(action);
  function openPane(next: Pane) {
    setPane(next);
    setMessage("");
    requestAnimationFrame(() => workflowTitle.current?.focus());
  }
  function showNote() {
    setTab("notes");
    setAddingNote(true);
    setNote("");
    requestAnimationFrame(() => notesTitle.current?.focus());
  }
  const activity = entries.filter((e) => e.customerId === customer.id);
  return (
    <Modal
      show
      onHide={onClose}
      backdrop="static"
      backdropClassName="action-workspace-backdrop"
      aria-label={`Action details for ${customer.name}`}
      className="action-workspace-modal"
      dialogClassName="action-workspace-dialog"
      contentClassName="action-workspace-content"
      container={() => document.querySelector(".admin-shell") as HTMLElement}
    >
      <div
        className={`action-workspace ${pane ? "action-workspace--paired" : ""}`}
      >
        {pane && (
          <section
            className="action-workspace-pane action-workspace-pane--work"
            aria-labelledby="action-workflow-title"
          >
            <header className="action-workspace-header">
              <h2 ref={workflowTitle} tabIndex={-1} id="action-workflow-title">
                {titles[pane]}
              </h2>
              <Button
                variant="link"
                className="btn-close"
                aria-label="Close action pane"
                onClick={() => setPane(null)}
              />
            </header>
            <Workflow
              key={pane}
              pane={pane}
              action={action}
              customer={customer}
              onCancel={() => setPane(null)}
              onSaved={(text) => {
                setMessage(text);
              }}
            />
          </section>
        )}
        <section
          className="action-workspace-pane action-workspace-pane--details"
          aria-label="Action details"
        >
          <Tab.Container activeKey={tab} onSelect={(key) => key && setTab(key)}>
            <header className="action-workspace-header">
              <Nav variant="pills" aria-label="Action detail tabs">
                {[
                  ["details", "Details"],
                  ["contacts", "Contacts"],
                  ["orders", "Submissions"],
                  ["notes", "Notes"],
                ].map(([key, label]) => (
                  <Nav.Item key={key}>
                    <Nav.Link
                      eventKey={key}
                      disabled={addingNote && key !== "notes"}
                    >
                      {label}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
              <Button
                variant="link"
                className="btn-close"
                aria-label="Close action details"
                onClick={onClose}
              />
            </header>
            <div className="action-workspace-scroll">
              {message && (
                <Alert variant="success" role="status">
                  {message}
                </Alert>
              )}
              {customer.onStop && (
                <Alert variant="danger" className="text-center fw-semibold">
                  ACCOUNT IS ON STOP
                </Alert>
              )}
              {action.closed && <Alert variant="info">Action completed.</Alert>}
              <Tab.Content>
                <Tab.Pane eventKey="details">
                  <div className="action-facts action-facts--three">
                    <Fact label="Account Number">{customer.account}</Fact>
                    <Fact label="Account Owner">{customer.owner}</Fact>
                    <Fact label="Customer Type">{customer.type}</Fact>
                  </div>
                  <hr />
                  <Fact label="Customer">
                    <h3 className="h4">{customer.name}</h3>
                  </Fact>
                  <Fact label="Contact">
                    {activity.find((e) => e.kind === "contact")?.text ||
                      customer.name}
                  </Fact>
                  <hr />
                  <div className="action-facts">
                    <Fact label="Next Action On">
                      {action.due.replace("T", " · ")}
                    </Fact>
                    <Fact label="Status (click to view)">
                      <Link
                        to={`/customers/${customer.id}?t=action`}
                        target="_blank"
                        rel="noreferrer"
                        className={`btn btn-sm btn-outline-${status === "Overdue" ? "danger" : "primary"}`}
                      >
                        {status}
                      </Link>
                    </Fact>
                  </div>
                  <hr />
                  <div className="action-facts">
                    <Fact label="Address">
                      {customer.address}
                      <br />
                      {customer.city}
                      <br />
                      {customer.postcode}
                    </Fact>
                    <div>
                      <Fact label="Contact Number">
                        <a href={`tel:${customer.phone}`}>
                          <FontAwesomeIcon icon={faPhone} /> {customer.phone}
                        </a>
                      </Fact>
                      <Fact label="Email Address">
                        <a href={`mailto:${customer.email}`}>
                          {customer.email}
                        </a>
                      </Fact>
                    </div>
                  </div>
                  <div className="action-facts action-facts--four">
                    <Fact label="Assigned To">{action.owner}</Fact>
                    <Fact label="Category">{customer.category}</Fact>
                    <Fact label="Type Of Action">{action.type}</Fact>
                    <Fact label="Preferred Action">
                      {customer.preferredContactMethod || "Not set"}
                    </Fact>
                  </div>
                  <div className="action-facts action-facts--four">
                    <Fact label="Call Day">{customer.callDay}</Fact>
                    <Fact label="Delivery Day">{customer.deliveryDay}</Fact>
                    <Fact label="Discount">{customer.discount}</Fact>
                    <Fact label="Grid Reference">{customer.grid}</Fact>
                  </div>
                  <hr />
                  {editing ? (
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        setActions(
                          actions.map((a) =>
                            a.id === action.id ? { ...a, text: actionText } : a,
                          ),
                        );
                        setEditing(false);
                        setMessage("Action text saved.");
                      }}
                    >
                      <Field
                        label="Action text"
                        type="textarea"
                        value={actionText}
                        onChange={setActionText}
                        required
                      />
                      <Button type="submit" size="sm">
                        Save action text
                      </Button>{" "}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setEditing(false)}
                      >
                        Cancel
                      </Button>
                    </Form>
                  ) : (
                    <>
                      <div className="d-flex align-items-center gap-2">
                        <span className="form-label mb-0">Action</span>
                        <Button
                          size="sm"
                          variant="link"
                          aria-label="Edit action text"
                          onClick={() => {
                            setActionText(action.text);
                            setEditing(true);
                          }}
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </Button>
                      </div>
                      <p className="action-text">{action.text}</p>
                    </>
                  )}
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      disabled={action.closed}
                      onClick={() => openPane("reschedule")}
                    >
                      <FontAwesomeIcon icon={faCalendarDays} /> Reschedule
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      disabled={action.closed}
                      onClick={() => openPane("reassign")}
                    >
                      <FontAwesomeIcon icon={faUser} /> Reassign
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => openPane("add")}
                    >
                      Add action
                    </Button>
                  </div>
                </Tab.Pane>
                <Tab.Pane eventKey="contacts">
                  <h3 className="h5">Customer contacts</h3>
                  <div className="activity">
                    <strong>{customer.name}</strong>
                    <p className="mt-2">
                      <a href={`tel:${customer.phone}`}>{customer.phone}</a>
                      <br />
                      <a href={`mailto:${customer.email}`}>{customer.email}</a>
                    </p>
                  </div>
                  {activity
                    .filter((e) => e.kind === "contact")
                    .map((e) => (
                      <div className="activity" key={e.id}>
                        <p className="action-text">{e.text}</p>
                      </div>
                    ))}
                </Tab.Pane>
                <Tab.Pane eventKey="orders">
                  <h3 className="h5">Previous submissions</h3>
                  {activity
                    .filter((e) => e.kind === "order")
                    .map((e) => (
                      <div className="activity" key={e.id}>
                        <small>{e.date}</small>
                        <p className="action-text">{e.text}</p>
                      </div>
                    ))}
                  {!activity.some((e) => e.kind === "order") && (
                    <p className="text-body-secondary">
                      No submissions recorded for this demo customer.
                    </p>
                  )}
                </Tab.Pane>
                <Tab.Pane eventKey="notes">
                  {addingNote ? (
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        setEntries([
                          ...entries,
                          {
                            id: crypto.randomUUID(),
                            customerId: customer.id,
                            kind: "note",
                            text: note,
                            date: new Date().toLocaleDateString("en-GB"),
                          },
                        ]);
                        setAddingNote(false);
                        setMessage("Note details saved.");
                      }}
                    >
                      <h3 className="h5" ref={notesTitle} tabIndex={-1}>
                        Add note
                      </h3>
                      <Field
                        label="Note details"
                        type="textarea"
                        value={note}
                        onChange={setNote}
                        required
                      />
                      <Button type="submit">Save note</Button>{" "}
                      <Button
                        variant="outline-secondary"
                        onClick={() => setAddingNote(false)}
                      >
                        Cancel
                      </Button>
                    </Form>
                  ) : (
                    <>
                      <h3 className="h5">Customer notes</h3>
                      {customer.notes && (
                        <div className="activity">
                          <small>General notes</small>
                          <p>{customer.notes}</p>
                        </div>
                      )}
                      {activity
                        .filter((e) => e.kind === "note")
                        .map((e) => (
                          <div className="activity" key={e.id}>
                            <small>{e.date}</small>
                            <p className="action-text">{e.text}</p>
                          </div>
                        ))}
                    </>
                  )}
                </Tab.Pane>
              </Tab.Content>
            </div>
            {!addingNote && (
              <footer className="action-workspace-footer">
                <div className="action-workspace-contact">
                  {customer.phone ? (
                    <a href={`tel:${customer.phone}`}>
                      <FontAwesomeIcon icon={faPhone} /> {customer.phone}
                    </a>
                  ) : (
                    <a href={`mailto:${customer.email}`}>
                      <FontAwesomeIcon icon={faEnvelope} /> {customer.email}
                    </a>
                  )}
                </div>
                <div className="action-workspace-buttons">
                  {action.closed ? (
                    <Button
                      variant="outline-primary"
                      onClick={() => {
                        setActions(
                          actions.map((a) =>
                            a.id === action.id ? { ...a, closed: false } : a,
                          ),
                        );
                        setMessage("Action reopened.");
                      }}
                    >
                      Reopen action
                    </Button>
                  ) : (
                    <Button onClick={() => openPane("complete")}>
                      Complete action
                    </Button>
                  )}
                  <Button variant="outline-primary" onClick={showNote}>
                    Add note
                  </Button>
                  {!customer.onStop ? (
                    <Button onClick={() => openPane("order")}>
                      New submission
                    </Button>
                  ) : (
                    session?.role && adminRoles.includes(session.role) && (
                      <Button
                        variant="outline-success"
                        onClick={() => {
                          setCustomers(
                            customers.map((c) =>
                              c.id === customer.id
                                ? { ...c, onStop: false }
                                : c,
                            ),
                          );
                          setMessage(
                            "Account stop removed in the demo workspace.",
                          );
                        }}
                      >
                        Remove Stop
                      </Button>
                    )
                  )}
                </div>
              </footer>
            )}
          </Tab.Container>
        </section>
      </div>
    </Modal>
  );
}
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="action-fact">
      <div className="form-label">{label}</div>
      <div>{children}</div>
    </div>
  );
}
function Workflow({
  pane,
  action,
  customer,
  onCancel,
  onSaved,
}: {
  pane: Pane;
  action: Action;
  customer: Customer;
  onCancel: () => void;
  onSaved: (text: string) => void;
}) {
  const { actions, setActions, entries, setEntries } = useDemo();
  const [followUp, setFollowUp] = useState(true);
  const [due, setDue] = useState(() => {
    if (pane !== "complete") return action.due;
    const next = new Date(`${action.due.slice(0, 10)}T12:00:00Z`);
    next.setUTCDate(next.getUTCDate() + 7);
    return next.toISOString().slice(0, 10) + action.due.slice(10);
  });
  const [showTime, setShowTime] = useState(action.due.includes("T"));
  const [type, setType] = useState(action.type);
  const [owner, setOwner] = useState(action.owner);
  const [text, setText] = useState(pane === "complete" ? action.text : "");
  const [orderForm, setOrderForm] = useState("");
  const [result, setResult] = useState("");
  const submitLabel =
    pane === "complete"
      ? "Complete"
      : pane === "add"
        ? "Save action"
        : pane === "order"
          ? "Submit demo form"
          : pane === "reschedule"
            ? "Reschedule"
            : "Reassign";
  function save() {
    if (pane === "complete" && action.closed) return;
    const newAction: Action = {
      id: crypto.randomUUID(),
      customerId: customer.id,
      type,
      owner,
      due,
      text,
      closed: false,
    };
    if (pane === "complete")
      setActions([
        ...actions.map((a) =>
          a.id === action.id ? { ...a, closed: true } : a,
        ),
        ...(followUp ? [newAction] : []),
      ]);
    if (pane === "add") setActions([...actions, newAction]);
    if (pane === "reschedule")
      setActions(actions.map((a) => (a.id === action.id ? { ...a, due } : a)));
    if (pane === "reassign")
      setActions(
        actions.map((a) => (a.id === action.id ? { ...a, owner } : a)),
      );
    if (pane === "order")
      setEntries([
        ...entries,
        {
          id: crypto.randomUUID(),
          customerId: customer.id,
          kind: "order",
          text: `${orderForm}\n${text}`,
          date: new Date().toLocaleDateString("en-GB"),
        },
      ]);
    const result =
      pane === "complete"
        ? followUp
          ? "Action completed and follow-up added."
          : "Action completed."
        : pane === "add"
          ? "New action saved."
          : pane === "reschedule"
            ? "Action rescheduled."
            : pane === "reassign"
              ? "Action reassigned."
              : "Demo submission saved.";
    setResult(result);
    onSaved(result);
  }
  if (result)
    return (
      <div className="action-workspace-scroll">
        <Alert variant="success" role="status">
          {result}
        </Alert>
      </div>
    );
  const newDetails = pane === "add" || (pane === "complete" && followUp);
  return (
    <Form
      className="action-workflow-form"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <div className="action-workspace-scroll">
        {pane === "complete" && (
          <>
            <Form.Check
              type="switch"
              id="close-with-new-action"
              className="my-4"
              label="Add new action when closing this one"
              checked={followUp}
              onChange={(e) => setFollowUp(e.target.checked)}
            />
            {followUp && <h3 className="h5">New action details</h3>}
          </>
        )}
        {newDetails && (
          <div className="action-facts action-workflow-customer">
            <div>
              <Fact label="Customer name">{customer.name}</Fact>
              <Fact label="Contact name">{customer.name}</Fact>
            </div>
            <Fact label="Address">
              {customer.address}
              <br />
              {customer.city}
              <br />
              {customer.postcode}
            </Fact>
          </div>
        )}
        <div className={newDetails ? "action-workflow-fields" : ""}>
          {(newDetails || pane === "reschedule") && (
            <>
              <Field
                label={pane === "reschedule" ? "Select new date" : "Due date"}
                type={showTime ? "datetime-local" : "date"}
                value={due}
                onChange={setDue}
                required
              />
              <Form.Check
                id="action-include-time"
                label="Include time"
                className="mb-3"
                checked={showTime}
                onChange={(e) => {
                  setShowTime(e.target.checked);
                  setDue(
                    e.target.checked
                      ? `${due.slice(0, 10)}T09:00`
                      : due.slice(0, 10),
                  );
                }}
              />
            </>
          )}
          {newDetails && (
            <Field
              label="Action type"
              options={[
                "Call",
                "Email",
                "SMS",
                "Visit",
                "WhatsApp",
                ...(type === "Telephone" ? ["Telephone"] : []),
              ]}
              value={type}
              onChange={setType}
            />
          )}
          {(newDetails || pane === "reassign") && (
            <Field
              label="Assign to"
              options={["Alex Morgan", "Jamie Taylor"]}
              value={owner}
              onChange={setOwner}
            />
          )}
          {newDetails && (
            <Field
              label="Action text"
              type="textarea"
              value={text}
              onChange={setText}
              required
            />
          )}
        </div>
        {pane === "order" && (
          <div className="action-order-layout">
            <div>
              <Form.Group controlId="order-form" className="mb-3">
                <Form.Label>Form</Form.Label>
                <Form.Select
                  required
                  value={orderForm}
                  onChange={(e) => setOrderForm(e.target.value)}
                >
                  <option value="">Select form</option>
                  <option>Standard customer order</option>
                </Form.Select>
              </Form.Group>
              <Accordion defaultActiveKey="customer">
                <Accordion.Item eventKey="customer">
                  <Accordion.Header>{customer.name}</Accordion.Header>
                  <Accordion.Body>
                    <p>
                      {customer.address}
                      <br />
                      {customer.city}
                      <br />
                      {customer.postcode}
                      <br />
                      {customer.phone}
                    </p>
                    <Fact label="Default Discount">{customer.discount}</Fact>
                    <Fact label="Call Day">{customer.callDay}</Fact>
                    <Fact label="Delivery Day">{customer.deliveryDay}</Fact>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </div>
            <div>
              <Alert variant="info">
                The source’s embedded form will be connected when the API
                is available. This form records a demo request only.
              </Alert>
              {orderForm && (
                <Field
                  label="Submission details"
                  type="textarea"
                  value={text}
                  onChange={setText}
                  required
                />
              )}
            </div>
          </div>
        )}
      </div>
      <footer className="action-workspace-footer d-flex justify-content-end gap-2">
        <Button variant="outline-secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={pane === "order" && !orderForm}>
          {submitLabel}
        </Button>
      </footer>
    </Form>
  );
}
